import {
  Avatar,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { CardContentError, CPButton, CPCard } from '@warden-ui/common-components';
import TeamLogoPlaceholder from '../assets/images/team_logo_placeholder.jpeg';
import { useTranslation } from 'react-i18next';

export interface TeamChooseInfo {
  id: number;
  name: string;
  domain: string;
  logo?: string | null;
  signInMethod: string[];
  ssoForcedEmailDomains: string[];
  isAdmin: boolean;
  hasJoined: boolean;
  useSelfSignup: boolean;
}

interface CardContentTeamChooseProps {
  teamInfo?: TeamChooseInfo[];
  onClickTeamChoose?: (team: TeamChooseInfo) => void;
  onClickNewTeam?: () => void;
  onClickTeamJoin?: (team: TeamChooseInfo) => void;
  errorMessage?: string;
  loading?: boolean;
  skeletonLoading?: boolean;
}

export const CardContentTeamChoose = ({
  teamInfo = [],
  onClickTeamChoose = () => {},
  skeletonLoading = false,
  ...props
}: CardContentTeamChooseProps) => {
  const [t] = useTranslation();

  if (skeletonLoading) {
    return (
      <CPCard showLogo={false}>
        <Stack spacing={2} className="w-100" sx={{ alignItems: "center" }}>
          <Skeleton variant="rounded" width={'80%'} height={36} />      { /* title */}
          <Skeleton variant="rounded" width={'100%'} height={68} />     { /* team info */}
          <Skeleton variant="rounded" width={'100%'} height={68} />     { /* team info */}
        </Stack>
      </CPCard>
    );
  }

  const adminChip = (
    <Chip label={t('chips.admin')} size="small" sx={{ marginLeft: 1, fontSize: 11, color: 'white', background: '#9C9C9C' }} />
  );

  const joinedTeams = teamInfo.filter(t => t.hasJoined);
  const joinableTeams = teamInfo.filter(t => !t.hasJoined);

  return (
    <>
    {joinedTeams.length === 0 &&
    <Box>
      <CardContentError
        size='small'
        message={t('errors.team_access_denied')}
      />
    </Box>
    }

    <CPCard showLogo={false}>
      {joinedTeams.length > 0 && 
      <>
      <Typography fontSize={24} sx={{ fontWeight: 'bold' }}>
        {t('team_choose.title')}
      </Typography>
      <List sx={{ width: '100%' }}>
          {joinedTeams.map((team) => (
            <ListItem
              onClick={() => onClickTeamChoose(team)}
              secondaryAction={<IconButton edge="end" aria-label="next">
                <ArrowForwardIosRoundedIcon />
              </IconButton>}
              disablePadding
            >
              <ListItemButton key={team.id} divider={true}>
                <ListItemIcon className="me-4">
                  <Avatar
                    variant="rounded"
                    src={team.logo ?? TeamLogoPlaceholder}
                    sx={{ width: 80, height: 40 }} />
                </ListItemIcon>
                <ListItemText
                  primary={<>
                    {team.name}
                    {team.isAdmin && adminChip}
                  </>}
                  secondary={team.domain} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </>
      }

      {joinedTeams.length > 0 && props.onClickNewTeam && <div style={{ marginTop: 24 }}></div>}

      {props.onClickNewTeam && <Stack
        direction="row"
        className='w-100'
        sx={{
          justifyContent: "space-between",
        }}
      >
        <Typography
          fontWeight={500}
          alignContent='center'
        > {t('team_choose.new_team.title')} </Typography>

        <CPButton
          label={t('team_choose.new_team.action')}
          variant="outlined"
          maxWidth={false}
          onClick={props.onClickNewTeam}
        >
        </CPButton>
      </Stack>}
    </CPCard>

    <div style={{ marginTop: 8 }}></div>

    {props.onClickTeamJoin && joinableTeams.length > 0 && 
    <CPCard showLogo={false}>
      <Typography fontSize={24} sx={{ fontWeight: 'bold' }}>
        {t('team_choose.join.title')}
      </Typography>
      <List sx={{ width: '100%' }}>
        {joinableTeams.map((team) => (
          <ListItem
            secondaryAction={
              <CPButton
                label={t('team_choose.join.action')}
                variant="text"
                onClick={() => props.onClickTeamJoin && props.onClickTeamJoin(team)}
              />
            }
            disablePadding
          >
            <ListItemButton key={team.id} divider={true}>
              <ListItemIcon className="me-4">
                <Avatar
                  variant="rounded"
                  src={team.logo ?? TeamLogoPlaceholder}
                  sx={{ width: 80, height: 40 }} />
              </ListItemIcon>
              <ListItemText
                primary={team.name}
                secondary={team.domain} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </CPCard>}
    </>
  );
};
